import { Injectable } from '@angular/core';
import {
    MultiRegistrationConfig,
    MultiregistrationController,
    UIState,
} from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';
import { ApiService } from '../api.service';
import { ShoppingCartResultUI } from '@azavista/payments/lib/shopping-cart';
import { ValidatorService } from '../validator.service';
import { AppLoggerService, LogLevel } from '../app-logger.service';

export type MultiRegistrationInitData = MultiRegistrationConfig & {
    cartResult: ShoppingCartResultUI;
};

const getParentRecursively = <T extends HTMLElement = HTMLElement>(
    element: HTMLElement,
    parentSelector: string
): T | undefined => {
    const parentElement = element.parentElement;
    const grandParent = parentElement?.parentElement;
    if (!grandParent) {
        return undefined;
    }
    const result = grandParent.querySelector<T>(parentSelector);
    if (result) {
        return result;
    }
    const documentAny: any = document;
    if (grandParent !== documentAny && parentElement !== documentAny) {
        return getParentRecursively(parentElement, parentSelector);
    }
};

@Injectable({
    providedIn: 'root',
})
export class MultiRegistrationService {
    controller?: MultiregistrationController | undefined;
    config?: MultiRegistrationConfig;
    uiState?: UIState;

    private readonly subParticipantIdAttrName = 'sub-participant-id';

    constructor(
        private apiSvc: ApiService,
        private validatorSvc: ValidatorService,
        private logSvc: AppLoggerService,
    ) {}

    async init(
        event_id: string,
        config: MultiRegistrationConfig,
        getCartResultFn: () => ShoppingCartResultUI,
        stateHandlerFn?: (state: UIState) => void
    ) {
        if (this.config?.workflow_id === config.workflow_id) {
            return;
        }
        this.controller = undefined;
        this.config = config;
        this.controller = new MultiregistrationController(event_id, config);
        const registrationData = await this.apiSvc.getMultiRegistration(
            this.config.workflow_id
        );
        this.controller.initialize(registrationData, getCartResultFn());
        this.subscribeToUiState(stateHandlerFn || ((state) => {}));
        this.validatorSvc.setSubParticipantValidatedCallback(
            (fieldId, value, validationResult, element) => {
                const subParticipantRootEl = getParentRecursively(
                    element,
                    `[${this.subParticipantIdAttrName}]`
                );
                const subParticipantId = subParticipantRootEl.getAttribute(
                    this.subParticipantIdAttrName
                );
                if (subParticipantId) {
                    this.controller.updateParticipant(subParticipantId, {
                        [fieldId]: validationResult.value,
                    });
                }
            }
        );
        this.validatorSvc.setMultiRegistrationTicketFieldValidatedCallback(
            (fieldId, validationResult, element) => {
                const subParticipantRootEl = getParentRecursively(
                    element,
                    `[${this.subParticipantIdAttrName}]`
                );
                const subParticipantId = subParticipantRootEl.getAttribute(
                    this.subParticipantIdAttrName
                );
                if (subParticipantId) {
                    this.controller.setSubParticipantTicket(
                        subParticipantId,
                        validationResult.value || null
                    );
                } else {
                    this.controller.setMainParticipantTicket(
                        validationResult.value || null
                    );
                }
            }
        );
    }

    subscribeToUiState(stateHandlerFn: (state: UIState) => void) {
        const subscription = this.controller?.getUIState();
        subscription.subscribe(() => {
            const state = subscription.getState();
            this.uiState = state;
            this.logSvc.log(LogLevel.log, 'Multi Registration State', state);
            stateHandlerFn(state);
        });
    }

    async submitMultiRegistration() {
        if (this.controller && !this.controller.hasErrors()) {
            try {
                const payload = this.controller.getSubmissionPayload();
                await this.apiSvc.updateMultiRegistration(
                    this.config.workflow_id,
                    payload
                );
                return true;
            } catch {
                return false;
            }
        }
        return false;
    }
}
